import React, { useEffect, useState, useRef } from "react";
import backendVerifiedUser from "../../../api/backendVerifiedUser";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

const LAYOUT_OPTIONS = [
  { label: "Classic", value: "classic" },
  { label: "Modern", value: "modern" },
];

export default function Themes() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [previewCss, setPreviewCss] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [formName, setFormName] = useState("");
  const [formLayout, setFormLayout] = useState("classic");
  const toast = useRef(null);

  const openEditForm = (theme) => {
    setEditingTheme(theme);
    setFormName(theme.name || "");
    setFormLayout(theme.layout_key || "classic");
    setShowForm(true);
  };

  const saveTheme = async () => {
    const body = { name: formName, layout_key: formLayout };
    try {
      if (editingTheme) {
        await backendVerifiedUser.put(`/admin/themes/${editingTheme.id}`, body);
      } else {
        await backendVerifiedUser.post("/admin/themes", body);
      }
      toast.current?.show({ severity: "success", summary: "Saved", detail: "Theme saved" });
      setShowForm(false);
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Save failed" });
    }
  };

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const resp = await backendVerifiedUser.get("/admin/themes");
      setThemes(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load themes" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchThemes();
  }, []);


  const activate = async (id) => {
    try {
      await backendVerifiedUser.put(`/admin/themes/${id}/activate`);
      toast.current?.show({ severity: "success", summary: "Activated", detail: "Theme activated" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Activation failed" });
    }
  };

  const uploadAsset = async (themeId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("asset", file);
    try {
      await backendVerifiedUser.post(`/admin/themes/${themeId}/upload-assets`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Asset uploaded" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Upload failed" });
    }
  };

  const previewTheme = async (theme) => {
    // Build a simple mockup HTML and fetch CSS asset content to show preview
    setPreviewCss("");
    setPreviewHtml("");
    if (!theme) return;
    const mockHtml = `
      <div class="preview-root">
        <header class="preview-header p-4">
          <h1>Site Header</h1>
        </header>
        <main class="preview-main p-6">
          <h2>Hero / Banner</h2>
          <p>Sample content to preview theme layout and styles.</p>
          <div class="preview-cards grid grid-cols-3 gap-4 mt-4">
            <div class="card p-4">Card 1</div>
            <div class="card p-4">Card 2</div>
            <div class="card p-4">Card 3</div>
          </div>
        </main>
        <footer class="preview-footer p-4">Site Footer</footer>
      </div>
    `;
    setPreviewHtml(mockHtml);

    if (theme.asset_path) {
      try {
        const resp = await fetch(theme.asset_path);
        if (!resp.ok) throw new Error('Could not fetch asset');
        const cssText = await resp.text();
        setPreviewCss(cssText);
      } catch (e) {
        console.error(e);
        toast.current?.show({ severity: "warn", summary: "Preview", detail: "Could not load asset for preview" });
      }
    } else {
      toast.current?.show({ severity: "info", summary: "Preview", detail: "No asset available for this theme" });
    }
    // scroll into view
    document.getElementById('theme-preview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="mx-4">
      <Toast ref={toast} />

      

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold">Available Themes</h3>
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {themes.length === 0 && <div>No themes found</div>}
            {themes.map((t) => {
              const files = t.files || [];
              const thumbnailFile = files.find(f => /\.(png|jpe?g|webp|gif|svg)$/i.test(f.name));
              const thumbnail = thumbnailFile ? thumbnailFile.url : null;
              const isActive = t.isActive === 1;
              return (
                <div
                  key={t.id}
                  className={`border rounded-lg overflow-hidden bg-white shadow-sm flex flex-col ${isActive ? 'ring-2 ring-green-500' : ''}`}
                >
                  <div className="relative w-full h-44 bg-gray-100 overflow-hidden flex items-center justify-center">
                    {thumbnail ? (
                      <img src={thumbnail} alt={t.name || `Theme ${t.id}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-gray-400 text-sm">No preview image</div>
                    )}
                    {isActive && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded">
                        Active
                      </span>
                    )}
                  </div>

                  <div className="p-3 flex flex-col flex-1">
                    <div className="text-base font-semibold">{t.name || `Theme ${t.id}`}</div>
                    <span className="inline-block mt-1 w-fit px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      Layout: {t.layout_key === 'modern' ? 'Modern' : 'Classic'}
                    </span>
                    {t.meta?.description && (
                      <div className="text-sm text-gray-600 mt-1">{t.meta.description}</div>
                    )}

                    <div className="mt-3 pt-3 border-t flex items-center gap-2">
                      <Button label="Edit" className="p-button-outlined p-button-sm" onClick={() => openEditForm(t)} />
                      {isActive ? (
                        <Button label="Active" className="p-button-success p-button-sm flex-1" disabled />
                      ) : (
                        <Button label="Activate" className="p-button-sm flex-1" onClick={() => activate(t.id)} />
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <Button label="Preview" className="p-button-text p-button-sm" onClick={() => previewTheme(t)} />
                      <label>
                        <input
                          type="file"
                          accept="image/*, text/css, .css, .zip"
                          onChange={(e) => uploadAsset(t.id, e.target.files[0])}
                          className="hidden"
                          id={`asset-${t.id}`}
                        />
                        <Button
                          label="Upload Asset"
                          className="p-button-text p-button-sm"
                          onClick={() => document.getElementById(`asset-${t.id}`).click()}
                        />
                      </label>
                    </div>

                    <Button
                      className="p-button-text p-button-plain p-button-sm mt-1 self-start"
                      label={expanded[t.id] ? 'Hide details' : 'Show details'}
                      onClick={() => setExpanded(prev => ({ ...prev, [t.id]: !prev[t.id] }))}
                    />
                    {expanded[t.id] && (
                      <div className="mt-2 text-xs text-gray-600 space-y-1">
                        <div>ID: {t.id}</div>
                        <div>Folder: {t.folder_name || '-'}</div>
                        <div>Asset: {t.asset_path ? <a href={t.asset_path} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open</a> : '-'}</div>
                        <div>Created: {t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</div>
                        <div>Updated: {t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}</div>
                        {files.length > 0 && (
                          <div>
                            Files:{' '}
                            {files.map((f, i) => (
                              <a key={f.url} href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 underline mr-2">
                                {f.name}
                              </a>
                            ))}
                          </div>
                        )}
                        <pre className="p-2 bg-gray-100 overflow-auto" style={{ maxHeight: 200 }}>{JSON.stringify(t, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div id="theme-preview" className="card p-4 mt-4">
        <h3 className="text-lg font-bold mb-3">Preview</h3>
        <div className="border p-3">
          {previewCss && <style>{previewCss}</style>}
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      </div>

      <Dialog
        header="Edit Theme"
        visible={showForm}
        style={{ width: "30rem" }}
        onHide={() => setShowForm(false)}
      >
        <div className="flex flex-col gap-4 pt-2">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <InputText
              className="w-full"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Theme name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Layout</label>
            <Dropdown
              className="w-full"
              value={formLayout}
              options={LAYOUT_OPTIONS}
              onChange={(e) => setFormLayout(e.value)}
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowForm(false)} />
            <Button label="Save" onClick={saveTheme} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
